<script setup lang="ts">
import LevelArrows from '@/components/icons/LevelArrows.vue'
import CareIcon from '@/components/references/CareIcon.vue'
import UIBrow from '@/components/ui/UIBrow.vue'
import UIHeading from '@/components/ui/UIHeading.vue'
import UIPill from '@/components/ui/UIPill.vue'
import UISticker from '@/components/ui/UISticker/UISticker.vue'
import UITextButton from '@/components/ui/UITextButton.vue'
import useLanguage, { useInlineLanguage } from '@/composables/useLanguage'
import { useReferencesStore } from '@/store/references'
import { colorHex } from '@/utils/colors'
import { getRemedySize, sortItemsByPoints } from '@/utils/remedy'
import { IRemedy, isISiteRemedy, ISiteRemedy } from '@lifk/types'
import { roundAndTruncate } from '@lifk/utils'
import {
  PhCubeTransparent,
  PhLock,
  PhMoney,
  PhWarning
} from '@phosphor-icons/vue'
import { computed } from 'vue'

const props = withDefaults(
  defineProps<{
    /** What remedy should be displayed? */
    remedy: ISiteRemedy | IRemedy
    /** What variant of the tooltip should be used? */
    variant?: 'sketch' | 'primer'
  }>(),
  {
    variant: 'sketch'
  }
)

const i18n = useLanguage('RemedyTooltip')
const { t } = useInlineLanguage()
const referencesStore = useReferencesStore()

const footprintWarningText = computed(() => {
  if (props.variant === 'primer' || !isISiteRemedy(props.remedy)) return null
  if (props.remedy.exceedsSiteFootprint) {
    return i18n.value.totalFootprintWarning
  }
  if (props.remedy.sumRemediesExceedsSiteFootprint) {
    return i18n.value.sumFootprintsWarning
  }
  return null
})

const remedySize = computed(() => getRemedySize(props.remedy.footprint))

const footprint = computed(() => roundAndTruncate(props.remedy.footprint))

const effects = computed(() => {
  const filteredEffects = props.remedy.effects.filter(
    (ef) => ef.level && ef.level.length > 0
  )
  const sortedEffects = sortItemsByPoints(filteredEffects, true)
  return sortedEffects.length > 5
    ? { overflow: true, items: filteredEffects.slice(0, 6) }
    : { overflow: false, items: filteredEffects }
})

const referencesLinkClicked = () => {
  referencesStore.openReference('remedy', props.remedy.key, 'mapped_remedy')
}

const creationText = computed(() => {
  if (!props.remedy.budgets) return undefined

  const creationCost = props.remedy.budgets.find(
    (cost) => cost.key === 'cost-creation'
  )
  if (!creationCost) return undefined

  return `~$${roundAndTruncate(creationCost.cost)}`
})

const careText = computed(() => {
  if (!props.remedy.budgets) return undefined

  const careCost = props.remedy.budgets.find((cost) => cost.key === 'cost-care')
  if (!careCost) return undefined

  return `~$${roundAndTruncate(careCost.cost)}${i18n.value.perYear}`
})

const footprintIsEditable = computed(() => {
  return props.remedy.footprintShape !== 'Object'
})
</script>

<template>
  <div class="pb-3 min-w-[230px]" data-testid="remedy-card">
    <div v-if="footprintWarningText" class="pt-3">
      <div class="flex items-center space-x-[5px]">
        <PhWarning size="20" class="fill-error" weight="fill" />
        <div
          class="text-emphasis text-body-xs tracking-wider whitespace-normal"
        >
          {{ footprintWarningText }}
        </div>
      </div>
      <div class="h-[1px] w-full bg-slate-tint-4 my-3"></div>
    </div>

    <!-- sketch variant titling -->
    <div
      v-if="props.variant == 'sketch'"
      :class="{
        'pt-3': !footprintWarningText
      }"
    >
      <div class="flex justify-between gap-2 max-w-full">
        <UIHeading
          tag="h4"
          variant="serif"
          class="mb-[5px] text-default min-w-0 whitespace-normal"
          :text="t(remedy.nameI18n)"
        />
        <UIPill
          :text="t(remedy.category.name)"
          size="sm"
          class="text-right ml-3 h-5"
          :raw-colors="{
            bgColor: remedy.category.categoryColor,
            textColor: colorHex('charcoal')
          }"
        />
      </div>
    </div>

    <!-- primer variant titling -->
    <div v-if="variant == 'primer'">
      <UISticker
        variant="L"
        class="hidden lg:block mb-4"
        sticker-type="with-back"
        :sticker-img="remedy.stickerImg"
        :has-shadow="true"
        :prevent-peel="true"
      />
      <div class="flex gap-[10px]">
        <UISticker
          variant="L"
          class="block lg:hidden"
          sticker-type="with-back"
          :sticker-img="remedy.stickerImg"
          :has-shadow="true"
          :prevent-peel="true"
        />
        <div class="w-full">
          <div class="flex justify-between space-x-[5px]">
            <h2 class="text-heading-sans font-bold">
              {{ t(remedy.nameI18n) }}
            </h2>
          </div>
          <UIBrow
            data-testid="remedy-category"
            :text="t(remedy.category.name)"
            size="sm"
            class="whitespace-normal text-left"
          />
        </div>
      </div>
    </div>

    <p
      class="font-sans text-body text-default w-full whitespace-normal text-left mt-[5px] mb-[10px]"
      :class="{ 'mb-3': !footprint && !remedy.footprintPlacement }"
      data-testid="remedy-description"
    >
      {{ t(remedy.descriptionI18n) }}
    </p>

    <div
      v-if="!!footprint && !!remedy.footprintPlacement"
      class="flex items-center justify-between mb-3"
      data-testid="remedy-specs"
    >
      <div class="flex flex-col gap-2">
        <div v-if="remedy.totalCost > 0" class="flex items-center gap-1">
          <PhMoney :size="20" class="fill-slate" />
          <p size="xl" class="text-body-xs tracking-wider">
            {{ creationText }}
          </p>
        </div>
        <div v-if="remedy.totalCost > 0" class="flex items-center gap-1">
          <CareIcon />
          <p size="xl" class="text-body-xs tracking-wider">
            {{ careText }}
          </p>
        </div>
      </div>

      <div class="flex flex-col gap-2">
        <div class="flex gap-1 items-center">
          <div
            class="border-slate border rounded-[2px] w-4 h-4 flex items-center justify-center"
            :class="{
              'border-dashed': footprintIsEditable
            }"
          >
            <UIBrow :text="remedySize" size="sm" class="leading-none" />
          </div>
          <p class="text-body-xs tracking-wider">{{ footprint }} sqft</p>
          <PhLock
            v-if="!footprintIsEditable"
            size="16"
            weight="fill"
            class="fill-charcoal shrink-0"
          />
        </div>
        <div class="flex items-center gap-1">
          <PhCubeTransparent :size="20" class="fill-slate" />
          <p class="text-body-xs tracking-wider">
            {{ remedy.footprintPlacement }}
          </p>
        </div>
      </div>
    </div>

    <div
      v-if="!!effects.items.length"
      class="bg-elevated rounded border border-slate-tint-5 mt-[15px] mb-3"
    >
      <div class="p-3 relative">
        <div
          v-for="effect in effects.items"
          :key="effect.key"
          class="flex justify-between items-center mb-[6px]"
        >
          <p class="text-body font-sans">{{ t(effect.nameI18n) }}</p>
          <LevelArrows :level="effect.level" :size="18" />
        </div>
        <div
          v-if="effects.overflow"
          class="h-[45px] w-[calc(100%-24px)] m-auto bg-gradient-to-t from-white to-transparent absolute bottom-0 left-3"
        ></div>
      </div>
    </div>
    <UITextButton
      icon="Book"
      variant="outline"
      color="neutral"
      :text="i18n.learnMore"
      @mousedown.stop.prevent="referencesLinkClicked"
    />
  </div>
</template>
